import { CmsEmptyState } from "@/components/ResearchCms";
import { l500LiveCards } from "../../utils/l500LiveCards";
import type { L500LiveItem } from "../../utils/l500LiveCards";
import type { SourceDataFirm } from "../../utils/sourceDataFirm";
import styles from "./L500LiveTab.module.css";
import { ErrorState, StatusBadge, UnknownState } from "./SourceDataStates";
import { useL500Live } from "./useSourceData";

type L500LiveTabProps = {
  firm: SourceDataFirm;
  editionId: string;
  researchBpaId: string;
  /** Requests only while true (default true) — the panel passes its open state. */
  enabled?: boolean;
};

function Item({ item }: { item: L500LiveItem }) {
  return (
    <li className={styles.item}>
      <div>
        <span className={styles.name}>{item.name}</span>
        {(item.meta.length > 0 || item.marker) && (
          <span className={styles.meta}>
            {[...item.meta, item.marker].filter(Boolean).join(" · ")}
          </span>
        )}
      </div>
      <StatusBadge label={item.status} testId="item-status" />
      {item.description && <p className={styles.description}>{item.description}</p>}
    </li>
  );
}

export function L500LiveTab({ firm, editionId, researchBpaId, enabled = true }: L500LiveTabProps) {
  const outcome = useL500Live({ firm, editionId, researchBpaId, enabled });

  if (outcome.kind === "loading") {
    return <CmsEmptyState loading message="Loading L500 Live content" />;
  }
  if (outcome.kind === "error") {
    return <ErrorState message="L500 Live content could not be loaded. Try again shortly." />;
  }
  if (outcome.kind === "unknown") {
    return (
      <UnknownState
        title="L500 Live content can't be looked up for this entry"
        sentence={outcome.sentence}
      />
    );
  }
  if (outcome.kind === "empty") {
    return <CmsEmptyState message="No Legal500 Live content for this firm in this practice area" />;
  }

  return (
    <div className={styles.tab}>
      {l500LiveCards(outcome.data).map(card => (
        <article key={card.id} className={styles.card}>
          <header className={styles.cardHead}>
            <h3 className={styles.level}>{card.level}</h3>
            <span className={styles.date}>{card.date}</span>
            <StatusBadge label={card.status} testId="approval-status" />
          </header>
          <div className={styles.cardBody}>
            {card.isEmpty && <p className={styles.empty}>No clients, matters or team members</p>}
            {card.groups.map(group => (
              <section key={group.heading} className={styles.group}>
                <h4 className={styles.groupHeading}>{group.heading}</h4>
                <ul aria-label={group.heading} className={styles.list}>
                  {group.items.map(item => (
                    <Item key={item.id} item={item} />
                  ))}
                </ul>
              </section>
            ))}
          </div>
        </article>
      ))}
    </div>
  );
}
